import aiSummaryService from './aiSummaryService';

const quizGeneratorService = {
  // 고품질 객관식 문제 생성을 위한 AI 프롬프트
  createQuizGenerationPrompt: (summaryData) => {
    return `당신은 교육 전문가이자 문제 출제 전문가입니다. 주어진 NCS 학습 내용을 바탕으로 고품질 객관식 문제를 생성해주세요.

**출제 원칙 (반드시 준수):**
1. ✅ **중요 학습 내용만 다룬다** - 핵심개념과 실무활용 중심
2. ✅ **하나의 문항당 하나의 사실만 질문** - 복합 개념 금지
3. ✅ **긍정문 우선** - 부정문 사용 시 굵게 강조
4. ✅ **설득력 있는 오답 포함** - 혼동 가능한 그럴듯한 오답
5. ✅ **보기에서 정답 단서 제거** - 내용 이해로만 정답 선택 가능
6. ✅ **보기 간 독립성 유지** - 서로 영향 없는 독립적 선택지
7. ✅ **보기 형태 통일** - 길이와 문장구조 유사하게
8. ✅ **반복 단어 제거** - 각 보기마다 다른 표현 사용

**학습 데이터:**
- 과목: ${summaryData.과목명}
- 토픽: ${summaryData.토픽명}
- 핵심개념: ${summaryData.핵심개념}
- 상세설명: ${summaryData.상세설명}
- 키워드: ${summaryData.키워드}
- 실무활용: ${summaryData.실무활용 || ''}
- 예시: ${summaryData.예시 || ''}

**요구사항:**
1. 위 데이터를 바탕으로 3개의 서로 다른 객관식 문제를 생성
2. 난이도: 하(1개), 중(1개), 상(1개)
3. 문제 유형: 정의/개념(1개), 실무적용(1개), 비교분석(1개)

**응답 형식 (JSON):**
{
  "questions": [
    {
      "question": "문제 내용 (명확하고 구체적)",
      "options": [
        "선택지 1 (길이와 형태 통일)",
        "선택지 2 (설득력 있는 오답)",
        "선택지 3 (혼동 가능한 오답)", 
        "선택지 4 (정답 - 명확하고 정확)"
      ],
      "correctAnswer": 4,
      "explanation": "정답 해설 (왜 정답인지, 오답은 왜 틀렸는지)",
      "difficulty": "하|중|상",
      "type": "정의|실무적용|비교분석",
      "keywords": ["관련", "키워드", "배열"]
    }
  ]
}

**예시 (참고용):**
문제: "객체지향 프로그래밍에서 캡슐화(Encapsulation)의 가장 적절한 설명은?"
1) 여러 객체를 하나로 묶어서 관리하는 기법
2) 데이터와 메서드를 하나로 묶고 외부 접근을 제한하는 원칙  ✓
3) 상위 클래스의 특성을 하위 클래스가 받아들이는 방식
4) 동일한 인터페이스로 서로 다른 동작을 수행하는 능력

지금 문제를 생성해주세요.`;
  },

  // AI를 통한 문제 생성
  generateQuestions: async (summaryData, progressCallback = null) => {
    try {
      const prompt = quizGeneratorService.createQuizGenerationPrompt(summaryData);
      
      if (progressCallback) progressCallback('AI 문제 생성 요청 중...');
      
      const response = await aiSummaryService.callAI(prompt, {
        responseSchema: {
          type: "object",
          properties: {
            questions: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  question: { type: "string" },
                  options: {
                    type: "array",
                    items: { type: "string" },
                    minItems: 4,
                    maxItems: 4
                  },
                  correctAnswer: { 
                    type: "integer",
                    minimum: 1,
                    maximum: 4 
                  },
                  explanation: { type: "string" },
                  difficulty: { 
                    type: "string",
                    enum: ["하", "중", "상"] 
                  },
                  type: { 
                    type: "string",
                    enum: ["정의", "실무적용", "비교분석"] 
                  },
                  keywords: {
                    type: "array",
                    items: { type: "string" }
                  }
                },
                required: ["question", "options", "correctAnswer", "explanation", "difficulty", "type", "keywords"]
              },
              minItems: 3,
              maxItems: 3
            }
          },
          required: ["questions"]
        }
      });

      if (progressCallback) progressCallback('문제 생성 완료!');
      
      return {
        success: true,
        questions: response.questions.map(q => ({
          ...q,
          // 메타 정보 추가
          sourceTopicId: summaryData.토픽ID,
          sourceTopicName: summaryData.토픽명,
          과목코드: summaryData.과목코드,
          과목명: summaryData.과목명,
          생성일시: new Date().toISOString(),
          출제빈도: 0,
          정답률: null
        }))
      };
      
    } catch (error) {
      console.error('문제 생성 실패:', error);
      return {
        success: false,
        error: error.message,
        questions: []
      };
    }
  },

  // 여러 토픽에서 배치 문제 생성
  generateBatchQuestions: async (summaryDataList, progressCallback = null) => {
    const allQuestions = [];
    const total = summaryDataList.length;
    
    for (let i = 0; i < summaryDataList.length; i++) {
      const summaryData = summaryDataList[i];
      
      if (progressCallback) {
        progressCallback(`문제 생성 중... (${i + 1}/${total}): ${summaryData.토픽명}`);
      }
      
      const result = await quizGeneratorService.generateQuestions(summaryData);
      
      if (result.success) {
        allQuestions.push(...result.questions);
      } else {
        console.warn(`토픽 "${summaryData.토픽명}" 문제 생성 실패:`, result.error);
      }
      
      // API 호출 간격 조절 (과부하 방지)
      if (i < summaryDataList.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
    
    return {
      success: allQuestions.length > 0,
      totalGenerated: allQuestions.length,
      questions: allQuestions
    };
  },

  // 문제 품질 검증
  validateQuestion: (question) => {
    const issues = [];
    
    // 필수 필드 검증
    if (!question.question?.trim()) issues.push('문제 내용이 비어있음');
    if (!Array.isArray(question.options) || question.options.length !== 4) {
      issues.push('선택지가 4개가 아님');
    }
    if (!question.correctAnswer || question.correctAnswer < 1 || question.correctAnswer > 4) {
      issues.push('정답 번호가 잘못됨');
    }
    if (!question.explanation?.trim()) issues.push('해설이 비어있음');
    
    // 선택지 품질 검증
    if (question.options) {
      // 길이 차이 검증 (너무 차이나면 안됨)
      const lengths = question.options.map(opt => opt.length);
      const maxLen = Math.max(...lengths);
      const minLen = Math.min(...lengths);
      if (maxLen - minLen > 50) {
        issues.push('선택지 길이 차이가 너무 큼');
      }
      
      // 중복 단어 검증
      const allWords = question.options.join(' ').toLowerCase();
      const wordCounts = {};
      allWords.split(/\s+/).forEach(word => {
        if (word.length > 2) { // 2글자 이상만 체크
          wordCounts[word] = (wordCounts[word] || 0) + 1;
        }
      });
      
      const repeatedWords = Object.entries(wordCounts)
        .filter(([word, count]) => count > 2)
        .map(([word]) => word);
      
      if (repeatedWords.length > 0) {
        issues.push(`반복 단어 발견: ${repeatedWords.join(', ')}`);
      }
    }
    
    return {
      isValid: issues.length === 0,
      issues: issues
    };
  }
};

export default quizGeneratorService;